/**
 * Returns the nearest *scrollable* ancestor.
 *
 * @param element - The element to start from.
 * @param options.excludeSelf - Whether to exclude the element itself from the search.
 * @param options.scrollDirection - The direction in which the scrollable element is configured to
 * scroll. This affects whether we look for a scroll ancestor based on overflow in the x direction
 * or the y direction.
 * @returns The nearest scrollable ancestor, or null if no scrollable ancestor is found.
 */
export function getScrollParentElement(
  element: HTMLElement | null,
  options: { excludeSelf?: boolean; scrollDirection?: "horizontal" | "vertical" } = {
    excludeSelf: false,
    scrollDirection: "vertical" as const,
  },
): HTMLElement | null {
  const optionsWithDefaults = {
    excludeSelf: false,
    scrollDirection: "vertical" as const,
    ...options,
  };

  let current: HTMLElement | null = optionsWithDefaults.excludeSelf ? (element?.parentElement ?? null) : element;

  while (current !== null) {
    const { overflowY, overflowX } = window.getComputedStyle(current);

    // A node is considered scrollable when overflow-y allows scrolling
    // _and_ its content is larger than its container.
    if (
      optionsWithDefaults.scrollDirection === "vertical" &&
      (overflowY === "auto" || overflowY === "scroll") &&
      current.scrollHeight > current.clientHeight
    ) {
      return current;
    }
    if (
      optionsWithDefaults.scrollDirection === "horizontal" &&
      (overflowX === "auto" || overflowX === "scroll") &&
      current.scrollWidth > current.clientWidth
    ) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
}
